import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Menu, Search, User, Settings, LogOut,
  ChevronDown,
  Building2,
  FilePlus2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useUIStore } from '@/stores/ui.store';
import { NotificationBell } from './NotificationBell';
import { Breadcrumb } from './Breadcrumb';
import { UiLanguageMenu } from '@/components/common/UiLanguageMenu';
import { resolveUploadsUrl } from '@/lib/resolve-uploads-url';

export function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { toggleSidebar, toggleCommandPalette, setTrainingRequestPanelOpen } = useUIStore();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggleCommandPalette();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleCommandPalette]);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate('/login');
  };

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : 'U';

  const role = user?.role != null ? String(user.role).toLowerCase().trim() : '';
  const showBackToInstitutionAdmin =
    role === 'institution_admin' || role === 'institution';

  const showTrainingRequestPanel = Boolean(user?.institutionId);
  const headerIconBtnClass =
    'rounded-lg p-2 text-[#5C5C5C] dark:text-[#EDE6DC] hover:bg-[#F0EBE3] dark:hover:bg-[hsl(217,33%,22%)] transition-colors';
  const userMenuItemClass =
    'flex w-full items-center justify-start gap-3 px-4 py-2.5 text-left text-sm text-[#1A1A1A] dark:text-[#EDE6DC] hover:bg-[#FDFBF7] dark:hover:bg-[hsl(217,33%,22%)] transition-colors [&_svg]:text-[#5C5C5C] dark:[&_svg]:text-[#E8D5BC]';

  return (
    <header
      data-trainer-header
      className="sticky top-0 z-20 flex h-16 items-center gap-4 border-b border-[color:var(--inst-player-border,#e5e7eb)] bg-[color:color-mix(in_srgb,var(--inst-player-surface,#ffffff)_92%,transparent)] px-4 backdrop-blur-md dark:border-[hsl(217,33%,22%)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-surface,hsl(217,33%,12%))_92%,transparent)] md:px-6"
    >
      {/* Mobile hamburger */}
      <button
        onClick={toggleSidebar}
        className={cn(headerIconBtnClass, 'md:hidden inline-flex items-center justify-center')}
        aria-label={t('header.toggleMenu', 'Toggle menu')}
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Breadcrumb */}
      <div className="hidden md:flex flex-1 items-center min-w-0">
        <Breadcrumb />
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-2 ms-auto">
        {/* Search trigger */}
        <button
          onClick={toggleCommandPalette}
          className="flex items-center gap-2 rounded-lg border border-[#E5DDD2] bg-[#FDFBF7] px-3 py-1.5 text-sm text-[#5C5C5C] transition-colors hover:bg-[#F0EBE3] dark:border-[hsl(217,33%,28%)] dark:bg-[hsl(217,33%,18%)] dark:text-[#EDE6DC] dark:hover:bg-[hsl(217,33%,22%)] [&_svg]:text-current"
          aria-label={t('header.search', 'Search')}
        >
          <Search className="h-4 w-4" />
          <span className="hidden lg:inline">{t('header.search', 'Search...')}</span>
          <kbd className="hidden lg:inline-flex items-center gap-0.5 rounded border border-[#E5DDD2] dark:border-gray-500 bg-white dark:bg-gray-600 px-1.5 py-0.5 text-[10px] font-mono text-[#5C5C5C] dark:text-[#EDE6DC]">
            ⌘K
          </kbd>
        </button>

        {/* Notifications */}
        <NotificationBell />

        <UiLanguageMenu
          icon="globe"
          buttonClassName={headerIconBtnClass}
        />

        {/* User menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 rounded-lg p-1.5 text-[#5C5C5C] transition-colors hover:bg-[#F0EBE3] dark:text-[#EDE6DC] dark:hover:bg-[hsl(217,33%,22%)]"
            aria-expanded={userMenuOpen}
            aria-haspopup="true"
            aria-label={t('header.userMenu', 'User menu')}
          >
            {user?.avatar ? (
              <img
                src={resolveUploadsUrl(user.avatar) ?? user.avatar}
                alt={t('header.userAvatar', 'User avatar')}
                className="h-8 w-8 rounded-full object-cover ring-2 ring-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_18%,transparent)] dark:ring-[color:color-mix(in_srgb,var(--inst-player-accent,#B38E5D)_45%,transparent)]"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_12%,transparent)] text-sm font-semibold text-[color:var(--inst-player-primary,#001B3D)] ring-2 ring-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_22%,transparent)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-accent,#B38E5D)_32%,hsl(217,33%,17%))] dark:text-white dark:ring-[color:color-mix(in_srgb,var(--inst-player-accent,#B38E5D)_55%,transparent)]">
                {initials}
              </div>
            )}
            <ChevronDown className="hidden h-4 w-4 text-[#5C5C5C] dark:text-[#EDE6DC] sm:block" aria-hidden />
          </button>

          {userMenuOpen && (
            <div
              role="menu"
              aria-label={t('header.userMenu', 'User menu')}
              onKeyDown={(e) => { if (e.key === 'Escape') setUserMenuOpen(false); }}
              className="absolute end-0 z-50 mt-2 w-56 rounded-lg border border-[#E5DDD2] bg-white py-1 shadow-xl dark:border-[hsl(217,33%,28%)] dark:bg-[hsl(217,33%,14%)]"
            >
              {/* User info */}
              <div className="border-b border-[#F0EBE3] px-4 py-3 dark:border-[hsl(217,33%,22%)]">
                <p className="text-sm font-medium text-[#001B3D] dark:text-gray-100 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-[#5C5C5C] dark:text-[#E8D5BC] truncate">{user?.email}</p>
              </div>

              <button
                role="menuitem"
                onClick={() => { navigate('/profile'); setUserMenuOpen(false); }}
                className={userMenuItemClass}
              >
                <User className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="text-left">{t('header.profile', 'Profile')}</span>
              </button>

              <button
                role="menuitem"
                onClick={() => { navigate('/settings'); setUserMenuOpen(false); }}
                className={userMenuItemClass}
              >
                <Settings className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="text-left">{t('header.settings', 'Settings')}</span>
              </button>

              {showTrainingRequestPanel ? (
                <button
                  role="menuitem"
                  onClick={() => {
                    setTrainingRequestPanelOpen(true);
                    setUserMenuOpen(false);
                  }}
                  className={userMenuItemClass}
                >
                  <FilePlus2 className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="text-left">{t('header.trainingContentRequest', 'Demande de formation (contenus)')}</span>
                </button>
              ) : null}

              {showBackToInstitutionAdmin ? (
                <button
                  role="menuitem"
                  onClick={() => {
                    navigate('/institution');
                    setUserMenuOpen(false);
                  }}
                  className={userMenuItemClass}
                >
                  <Building2 className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="text-left">{t('header.backToInstitutionAdmin', 'Administration de l’établissement')}</span>
                </button>
              ) : null}

              <div className="mt-1 border-t border-[#F0EBE3] pt-1 dark:border-[hsl(217,33%,22%)]" role="separator">
                <button
                  role="menuitem"
                  onClick={handleLogout}
                  className="flex w-full items-center justify-start gap-3 px-4 py-2.5 text-left text-sm text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                >
                  <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="text-left">{t('header.logout', 'Logout')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
